// SdkRegisterView.tsx
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
    Form,
    Input,
    Button,
    Card,
    Typography,
    Row,
    Col,
    Alert,
    Space,
    message,
} from 'antd';
import {
    UserOutlined,
    AppstoreOutlined,
    MailOutlined,
    LockOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import { Link } from 'react-router-dom';
import { ROOT } from '../services/Api';
import { sdkMgmt } from '../server/SdkMgmt';

const { Title, Text } = Typography;

interface RegisterFormData {
    appName: string;
    packageName: string;
    email: string;
    password: string;
    confirmPassword?: string;
}

interface RegisterResponse {
    success: boolean;
    message?: string;
    data?: any;
}

// 定义 Props 类型（包含 WithTranslation 和自定义 props）
interface SdkRegisterViewProps extends WithTranslation {
    onRegisterSuccess?: (data: any) => void;
    onRegisterFail?: (error: any) => void;
    loading?: boolean;
}

// 定义 State 类型
interface SdkRegisterViewState {
    loading: boolean;
    errorMessage: string;
}

class SdkRegisterView extends React.Component<SdkRegisterViewProps, SdkRegisterViewState> {
    formRef = React.createRef<FormInstance>();

    // ✅ 明确声明 state 的类型
    state: SdkRegisterViewState = {
        loading: false,
        errorMessage: '',
    };

    // 验证包名格式
    validatePackageName = (_: any, value: string) => {
        const packageRegex = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
        if (!value) {
            return Promise.reject(new Error('请输入应用包名'));
        }
        if (!packageRegex.test(value)) {
            return Promise.reject(new Error('包名格式不正确（如 com.example.app）'));
        }
        return Promise.resolve();
    };

    // 验证密码强度
    validatePassword = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error('请设置登录密码'));
        }
        if (value.length < 8) {
            return Promise.reject(new Error('密码长度不能少于8位'));
        }
        if (!/[A-Z]/.test(value)) {
            return Promise.reject(new Error('密码需包含至少一个大写字母'));
        }
        if (!/[a-z]/.test(value)) {
            return Promise.reject(new Error('密码需包含至少一个小写字母'));
        }
        if (!/\d/.test(value)) {
            return Promise.reject(new Error('密码需包含至少一个数字'));
        }
        return Promise.resolve();
    };

    // 验证确认密码
    validateConfirmPassword = (_: any, value: string) => {
        const password = this.formRef.current?.getFieldValue('password');
        if (!value) {
            return Promise.reject(new Error('请确认密码'));
        }
        if (value !== password) {
            return Promise.reject(new Error('两次输入的密码不一致'));
        }
        return Promise.resolve();
    };

    // 提交表单
    handleSubmit = async (values: RegisterFormData) => {
        // const DEBUG = false;
        // const TAG = `SdkRegisterView.handleSubmit() - `;
        // this.setState({ loading: true, errorMessage: '' });

        // try {
        //     const { appName, packageName, email, password } = values;

        //     if (DEBUG) {
        //         console.log(TAG, `appName:`, appName);
        //         console.log(TAG, `packageName:`, packageName);
        //         console.log(TAG, `email:`, email);
        //         console.log(TAG, `password:`, password);
        //     }

        //     let response = await sdkMgmt.register(appName, packageName, email, password);

        //     const result: RegisterResponse = response;
        //     DEBUG && console.log(TAG, `result:\n`, result);

        //     if (result.success) {
        //         message.success({
        //             content: '注册成功!',
        //             icon: <CheckCircleOutlined />,
        //             duration: 3,
        //         });

        //         // TODO: 转到管理界面

        //         // this.props.onRegisterSuccess?.(result.data);
        //         // this.formRef.current?.resetFields();
        //     } else {
        //         const errorMsg = result.message || '注册失败，请稍后重试';
        //         this.setState({ errorMessage: errorMsg });
        //         message.error(errorMsg);
        //         this.props.onRegisterFail?.(result);
        //     }
        // } catch (error: any) {
        //     const errorMsg = error.message || '网络错误，请稍后重试';
        //     this.setState({ errorMessage: errorMsg });
        //     message.error(errorMsg);
        //     this.props.onRegisterFail?.(error);
        // } finally {
        //     this.setState({ loading: false });
        // }
    };

    render() {
        const { t, loading: parentLoading } = this.props;
        const { loading, errorMessage } = this.state;

        return (
            <div className="sdk-register-page">
                <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
                    <Col xs={22} sm={16} md={12} lg={8} xl={6}>
                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <div style={{ textAlign: 'center', marginBottom: 12 }}>
                                {/* <div
                                    style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 12,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: 16,
                                    }}
                                >
                                    <AppstoreOutlined style={{ fontSize: 28, color: '#fff' }} />
                                </div> */}
                                <Title level={3} style={{ marginBottom: 0 }}>
                                    {t('sdk.register.title')}
                                </Title>
                                <Text type="secondary">
                                    {t('sdk.register.subtitle')}
                                </Text>
                            </div>

                            {errorMessage && (
                                <Alert
                                    message={errorMessage}
                                    type="error"
                                    showIcon
                                    closable
                                    style={{ marginBottom: 24 }}
                                    onClose={() => this.setState({ errorMessage: '' })}
                                />
                            )}

                            <Form
                                ref={this.formRef}
                                name="register"
                                onFinish={this.handleSubmit}
                                layout="vertical"
                                size="large"
                                requiredMark={false}
                            >
                                <Form.Item
                                    name="appName"
                                    label="应用名称"
                                    rules={[
                                        { required: true, message: '请输入应用名称' },
                                        { min: 2, message: '应用名称至少2个字符' },
                                        { max: 50, message: '应用名称不能超过50个字符' },
                                        {
                                            pattern: /^[\u4e00-\u9fa5a-zA-Z0-9_\-\s]+$/,
                                            message: '应用名称包含非法字符',
                                        },
                                    ]}
                                    extra="应用名称不能与已有应用重复"
                                    tooltip="建议使用易识别的名称"
                                >
                                    <Input
                                        prefix={<AppstoreOutlined style={{ color: '#bfbfbf' }} />}
                                        placeholder="请输入应用名称"
                                        autoComplete="off"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="packageName"
                                    label="应用包名"
                                    rules={[
                                        { required: true, message: '请输入应用包名' },
                                        { validator: this.validatePackageName },
                                    ]}
                                    extra="格式：com.example.app"
                                >
                                    <Input
                                        prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                        placeholder="请输入应用包名"
                                        autoComplete="off"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="email"
                                    label="电子邮箱"
                                    rules={[
                                        { required: true, message: '请输入电子邮箱' },
                                        { type: 'email', message: '邮箱格式不正确' },
                                    ]}
                                    extra="用于接收验证码和登录账号"
                                >
                                    <Input
                                        prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                                        placeholder="请输入电子邮箱"
                                        autoComplete="email"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    label="登录密码"
                                    rules={[
                                        { required: true, message: '请设置登录密码' },
                                        { validator: this.validatePassword },
                                    ]}
                                    extra="至少8位，需包含大小写字母和数字"
                                >
                                    <Input.Password
                                        prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                        placeholder="请设置登录密码"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="confirmPassword"
                                    label="确认密码"
                                    rules={[
                                        { required: true, message: '请确认密码' },
                                        { validator: this.validateConfirmPassword },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                        placeholder="请再次输入密码"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>

                                <Form.Item style={{ marginBottom: 16 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        loading={loading || parentLoading}
                                        size="large"
                                        style={{
                                            height: 44,
                                            borderRadius: 8,
                                            fontSize: 16,
                                        }}
                                    >
                                        {loading || parentLoading ? '注册中...' : '注 册'}
                                    </Button>
                                </Form.Item>

                                <div style={{ textAlign: 'center' }}>
                                    <Space>
                                        <Text type="secondary">已有账号？</Text>
                                        <Link to={`${ROOT}/sdkLogin`} style={{ textDecoration: 'none' }}>
                                            <Button type="link" style={{ padding: 0 }}>
                                                立即登录
                                            </Button>
                                        </Link>
                                    </Space>
                                </div>
                            </Form>
                        </Card>

                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                © 2026 SDK Platform. All rights reserved.
                            </Text>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }
}

export const SdkRegisterPage = withTranslation()(SdkRegisterView);